if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        if (this.dataset.desktopLayout === 'thumbnail_slider') {
          this.initSwiperGallery();
        } else {
          this.initDefaultGallery();
        }
      }

      initDefaultGallery() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initSwiperGallery() {
        if (typeof Swiper === 'undefined') return;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          mainEl: this.querySelector('.product-swiper-gallery__main'),
          thumbsEl: this.querySelector('.product-swiper-gallery__thumbs'),
        };

        if (!this.elements.mainEl) return;

        const slideCount = this.elements.mainEl.querySelectorAll('.swiper-slide[data-media-id]').length;
        this.rebuildSwiperIndex();

        if (slideCount <= 1) return;

        if (this.elements.thumbsEl) {
          this.thumbsSwiper = new Swiper(this.elements.thumbsEl, {
            direction: 'vertical',
            slidesPerView: 7,
            spaceBetween: 5,
            centeredSlides: true,
            centeredSlidesBounds: true,
            watchSlidesProgress: true,
            slideToClickedSlide: true,
            watchOverflow: true,
          });
        }

        this.mainSwiper = new Swiper(this.elements.mainEl, {
          effect: 'fade',
          fadeEffect: { crossFade: true },
          observer: true,
          observeParents: true,
          watchOverflow: true,
          preventInteractionOnTransition: true,
          navigation: {
            nextEl: this.elements.mainEl.querySelector('.swiper-button-next'),
            prevEl: this.elements.mainEl.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.elements.mainEl.querySelector('.swiper-pagination'),
            clickable: true,
          },
          thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
        });

        this.mainSwiper.on('slideChangeTransitionStart', () => {
          if (this.thumbsSwiper && !this.thumbsSwiper.destroyed) {
            this.thumbsSwiper.slideTo(this.mainSwiper.activeIndex);
          }
          this.onSwiperSlideChange();
        });

        this.mainSwiper.on('slideChangeTransitionEnd', () => {
          const activeSlide = this.getActiveSwiperSlide();
          if (activeSlide) this.playActiveMedia(activeSlide);
        });

        this.mainSwiper.slideTo(0, 0);
        this.thumbsSwiper?.slideTo(0, 0);
      }

      getActiveSwiperSlide() {
        return (
          this.elements.mainEl?.querySelector('.swiper-slide.swiper-slide-active') ||
          this.elements.mainEl?.querySelector('.swiper-slide[data-media-id]')
        );
      }

      rebuildSwiperIndex() {
        if (!this.elements?.mainEl) return;
        this.mediaIdToIndex = {};
        this.elements.mainEl.querySelectorAll('.swiper-slide[data-media-id]').forEach((slide, index) => {
          this.mediaIdToIndex[slide.dataset.mediaId] = index;
        });
        this.mainSwiper?.update();
        this.thumbsSwiper?.update();
      }

      onSwiperSlideChange() {
        const activeSlide = this.getActiveSwiperSlide();
        if (!activeSlide || !this.elements.thumbsEl) return;

        const mediaId = activeSlide.dataset.mediaId;
        const activeThumbnail = this.elements.thumbsEl.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.announceLiveRegion(activeSlide, activeThumbnail.dataset.mediaPosition);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.mainSwiper) {
          this.setActiveMediaSwiper(mediaId, prepend);
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveMediaSwiper(mediaId, prepend) {
        const activeSlide = this.elements.mainEl.querySelector(`[data-media-id="${mediaId}"]`);

        if (prepend && activeSlide) {
          activeSlide.parentElement.prepend(activeSlide);

          if (this.elements.thumbsEl) {
            const activeThumbnail = this.elements.thumbsEl.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          this.rebuildSwiperIndex();
        }

        const index = this.mediaIdToIndex?.[mediaId];
        if (index !== undefined && this.mainSwiper) {
          this.mainSwiper.slideTo(index, prepend ? 0 : undefined);
        }

        this.preventStickyHeader();

        if (activeSlide) {
          window.setTimeout(() => {
            const activeMediaRect = activeSlide.getBoundingClientRect();
            if (activeMediaRect.top > -0.5) return;
            window.scrollTo({ top: activeMediaRect.top + window.scrollY, behavior: 'smooth' });
          });
          this.playActiveMedia(activeSlide);

          if (this.elements.thumbsEl) {
            const activeThumbnail = this.elements.thumbsEl.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              this.announceLiveRegion(activeSlide, activeThumbnail.dataset.mediaPosition);
            }
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
